import { useMemo } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import MethodBadge from '@/components/layout/MethodBadge';
import type { SemanticHit } from '@/hooks/useSemanticSearch';
import type { Method } from '@/lib/tokens';

interface Props {
    query: string;
    results: SemanticHit[];
    busy: boolean;
    error: string | null;
    activeIdx: number;
    onActiveChange: (idx: number) => void;
    onSelect: (operationId: string) => void;
}

export default function SemanticTab({ query, results, busy, error, activeIdx, onActiveChange, onSelect }: Props) {

    // Normalise scores to 0..1 for the bar (cosine sim can technically go neg).
    const normalized = useMemo(() => {
        if (!results.length) return [];
        const max = Math.max(...results.map((r) => r.score));
        const min = Math.min(0, Math.min(...results.map((r) => r.score)));
        const span = Math.max(max - min, 1e-6);
        return results.map((r) => ({ ...r, normScore: (r.score - min) / span }));
    }, [results]);

    if (!query.trim()) {
        return (
            <div className="px-4 py-10 text-center text-sm text-muted flex flex-col items-center gap-2">
                <Sparkles className="h-5 w-5 text-ghost" />
                <div>Cari endpoint berdasarkan makna, bukan kata.</div>
                <div className="text-xs text-ghost">Coba: "buat tugas berulang" atau "kirim email"</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="px-4 py-10 text-center text-sm">
                <div className="text-amber-400 mb-1">{error}</div>
                {error.includes('Login') && (
                    <div className="text-xs text-ghost">Buka /settings untuk generate API key.</div>
                )}
            </div>
        );
    }

    if (busy && results.length === 0) {
        return (
            <div className="px-4 py-10 text-center text-sm text-muted flex flex-col items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Mencari makna...</span>
            </div>
        );
    }

    if (!normalized.length) {
        return (
            <div className="px-4 py-10 text-center text-sm text-muted">
                Tidak ada hasil semantic untuk "{query}"
            </div>
        );
    }

    return (
        <div className="py-1" data-testid="semantic-results">
            <div className="px-4 pt-1 pb-1 text-[10px] uppercase tracking-wider text-ghost font-semibold flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Hasil Semantic
                {busy && <Loader2 className="h-3 w-3 animate-spin ml-1" />}
            </div>
            <div>
                {normalized.map((hit, idx) => (
                    <SemanticRow
                        key={hit.operationId}
                        hit={hit}
                        active={idx === activeIdx}
                        onMouseEnter={() => onActiveChange(idx)}
                        onClick={() => onSelect(hit.operationId)}
                    />
                ))}
            </div>
        </div>
    );
}

function SemanticRow({
    hit,
    active,
    onMouseEnter,
    onClick,
}: {
    hit: SemanticHit & { normScore: number };
    active: boolean;
    onMouseEnter: () => void;
    onClick: () => void;
}) {
    const method = hit.method.toUpperCase() as Method;
    const pct = Math.round(hit.normScore * 100);
    return (
        <div
            role="option"
            aria-selected={active}
            data-testid={`semantic-row-${hit.operationId}`}
            data-active={active ? 'true' : 'false'}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            className="flex items-center gap-2.5 px-4 py-2 cursor-pointer transition-colors"
            style={{
                backgroundColor: active ? 'var(--surface-hover)' : 'transparent',
                borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
                paddingLeft: active ? '14px' : '16px',
            }}
        >
            <MethodBadge method={method} size="xs" />
            <div className="flex flex-col min-w-0 flex-1">
                <span className="truncate text-ink text-sm">{hit.summary}</span>
                <span className="truncate text-[11px] text-muted font-mono">{hit.path}</span>
            </div>
            <div className="flex flex-col items-end gap-0.5 shrink-0 w-16">
                <div className="text-[10px] text-ghost tabular-nums">{pct}%</div>
                <div className="w-full h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border)' }}>
                    <div
                        className="h-full rounded-full"
                        style={{
                            width: `${pct}%`,
                            backgroundColor: pct > 70 ? 'var(--accent)' : pct > 40 ? 'var(--warn)' : 'var(--ghost)',
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
